"use client";

import { c as _c } from "react/compiler-runtime";
import Image from "next/image";
import { useMemo } from "react";
import { hasData } from "@/helpers/has-data";
import { Section } from "@/components/section";
import { Breadcrumbs } from "@/components/common/breadcrumbs";
import { CallToAction } from "@/components/common/call-to-action";
import { Text } from "@/components/ui/text";
import { SeoHeading } from "@/components/common/seo-heading";
import { Box } from "@/components/ui/box";
import { DateDisplay } from "@/components/common/date-display";
import { RenderIf } from "@/components/common/render-if";
import { ArrowRight, Download } from "lucide-react";
import { cn } from "@/lib/utils";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { usePopup } from "@/providers/popup-provider";
type AccreditationsType = {
  imagePath?: string;
  text?: string;
};
export type BannerWithBackgroundColorContentType = {
  imagePath?: string;
  imageAltText?: string;
  primaryButtonAction?: string;
  primaryButtonText?: string;
  secondaryButtonAction?: string;
  secondaryButtonText?: string;
  seoHeading?: string;
  heading: string;
  paragraph?: string;
  color?: "orange" | "blue" | "rose";
  showCreatedDate: boolean;
  breadcrumbs: BreadcrumbsType[];
  logos?: AccreditationsType[];
  backgroundBannerImage?: string;
  createdAt?: string;
  updatedAt?: string;
};
type BannerWithBackgroundColorProps = {
  section: PageSections;
  data: BannerWithBackgroundColorContentType;
};
const BannerWithBackgroundColor = props => {
  const $ = _c(71);
  const {
    data,
    section
  } = props;
  const {
    openPopup
  } = usePopup();
  let t0;
  if ($[0] !== data.primaryButtonAction) {
    t0 = data.primaryButtonAction && data.primaryButtonAction?.endsWith("_POPUP");
    $[0] = data.primaryButtonAction;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const hasPrimaryPopup = t0;
  let t1;
  if ($[2] !== data.secondaryButtonAction) {
    t1 = data.secondaryButtonAction && data.secondaryButtonAction?.endsWith("_POPUP");
    $[2] = data.secondaryButtonAction;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const hasSecondaryPopup = t1;
  if (!hasData(data)) {
    return null;
  }
  const {
    imageAltText: t2,
    imagePath,
    color: t3,
    heading,
    seoHeading,
    paragraph,
    breadcrumbs,
    logos,
    primaryButtonAction,
    primaryButtonText,
    secondaryButtonAction,
    secondaryButtonText,
    showCreatedDate,
    backgroundBannerImage,
    updatedAt
  } = data;
  const imageAltText = t2 === undefined ? "image" : t2;
  const color = t3 === undefined ? "orange" : t3;
  let T0;
  let t4;
  let t5;
  if ($[4] !== color || $[5] !== section.sectionClass || $[6] !== section.sectionId) {
    const bgColorMapping = {
      orange: "bg-orange-50",
      blue: "bg-blue-50",
      rose: "bg-rose-50"
    };
    T0 = Section;
    t4 = section.sectionId;
    t5 = cn(section.sectionClass, bgColorMapping[color], "bg-background relative overflow-hidden bg-left md:bg-center lg:bg-right");
    $[4] = color;
    $[5] = section.sectionClass;
    $[6] = section.sectionId;
    $[7] = T0;
    $[8] = t4;
    $[9] = t5;
  } else {
    T0 = $[7];
    t4 = $[8];
    t5 = $[9];
  }
  let t6;
  let t7;
  if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = {
      className: "hidden sm:block dark:blur-xl",
      loading: "eager",
      fetchPriority: "high"
    };
    t7 = <div className="from-background absolute inset-0 -z-1 hidden bg-linear-[60deg] to-transparent drop-shadow-2xl sm:from-60% dark:block" />;
    $[10] = t6;
    $[11] = t7;
  } else {
    t6 = $[10];
    t7 = $[11];
  }
  let t8;
  if ($[12] !== heading || $[13] !== seoHeading) {
    t8 = <SeoHeading as="h1" heading={heading} seoHeading={seoHeading} />;
    $[12] = heading;
    $[13] = seoHeading;
    $[14] = t8;
  } else {
    t8 = $[14];
  }
  const t9 = !!paragraph;
  let t10;
  if ($[15] !== paragraph) {
    t10 = <Text>{paragraph}</Text>;
    $[15] = paragraph;
    $[16] = t10;
  } else {
    t10 = $[16];
  }
  let t11;
  if ($[17] !== t10 || $[18] !== t9) {
    t11 = <RenderIf condition={t9}>{t10}</RenderIf>;
    $[17] = t10;
    $[18] = t9;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== updatedAt) {
    t12 = <Text className="text-muted-foreground tracking-wide">Last Updated on <DateDisplay dateString={updatedAt} /></Text>;
    $[20] = updatedAt;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== showCreatedDate || $[23] !== t12) {
    t13 = <RenderIf condition={showCreatedDate}>{t12}</RenderIf>;
    $[22] = showCreatedDate;
    $[23] = t12;
    $[24] = t13;
  } else {
    t13 = $[24];
  }
  let t14;
  let t15;
  if ($[25] === Symbol.for("react.memo_cache_sentinel")) {
    t14 = <ArrowRight />;
    t15 = <Download />;
    $[25] = t14;
    $[26] = t15;
  } else {
    t14 = $[25];
    t15 = $[26];
  }
  let t16;
  if ($[27] !== hasPrimaryPopup || $[28] !== openPopup) {
    t16 = () => {
      if (hasPrimaryPopup) {
        openPopup({
          type: "download-brochure"
        });
      }
    };
    $[27] = hasPrimaryPopup;
    $[28] = openPopup;
    $[29] = t16;
  } else {
    t16 = $[29];
  }
  let t17;
  if ($[30] !== hasSecondaryPopup || $[31] !== openPopup) {
    t17 = () => {
      if (hasSecondaryPopup) {
        openPopup({
          type: "download-brochure"
        });
      }
    };
    $[30] = hasSecondaryPopup;
    $[31] = openPopup;
    $[32] = t17;
  } else {
    t17 = $[32];
  }
  const t18 = hasPrimaryPopup ? undefined : primaryButtonAction || "#";
  const t19 = hasSecondaryPopup ? undefined : secondaryButtonAction || "#";
  let t20;
  if ($[33] !== primaryButtonText || $[34] !== secondaryButtonText || $[35] !== t16 || $[36] !== t17 || $[37] !== t18 || $[38] !== t19) {
    t20 = <CallToAction size="lg" onSaveText={primaryButtonText} onCancelText={secondaryButtonText} onSaveIcon={t14} onCancelIcon={t15} onSave={t16} onCancel={t17} onSaveLink={t18} onCancelLink={t19} />;
    $[33] = primaryButtonText;
    $[34] = secondaryButtonText;
    $[35] = t16;
    $[36] = t17;
    $[37] = t18;
    $[38] = t19;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  let t21;
  if ($[40] !== data.logos || $[41] !== logos) {
    t21 = logos && logos.length > 0 && <Box className="my-4 sm:my-6 lg:my-8"><Box className="relative inline-flex flex-wrap items-center gap-3">{data?.logos?.map(_temp)}</Box></Box>;
    $[40] = data.logos;
    $[41] = logos;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  let t22;
  if ($[43] !== t11 || $[44] !== t13 || $[45] !== t20 || $[46] !== t21 || $[47] !== t8) {
    t22 = <div className="group space-y-8 sm:col-span-8">{t8}{t11}{t13}{t20}{t21}</div>;
    $[43] = t11;
    $[44] = t13;
    $[45] = t20;
    $[46] = t21;
    $[47] = t8;
    $[48] = t22;
  } else {
    t22 = $[48];
  }
  let t23;
  if ($[49] !== backgroundBannerImage || $[50] !== data.heading) {
    t23 = backgroundBannerImage && <div className="group drop-shadow-primary overflow-hidden rounded-3xl bg-white/10 p-3 opacity-0 drop-shadow-xl dark:opacity-100"><AspectRatio ratio={1} className="h-full w-full transform overflow-hidden rounded-2xl transition-transform duration-700 group-hover:scale-110"><Image src={backgroundBannerImage} quality={100} alt={data.heading} className="transform object-cover object-right bg-blend-color transition-transform duration-700 group-hover:scale-[1.03]" fill={true} sizes="400px" loading="eager" fetchPriority="high" /></AspectRatio></div>;
    $[49] = backgroundBannerImage;
    $[50] = data.heading;
    $[51] = t23;
  } else {
    t23 = $[51];
  }
  let t24;
  if ($[52] !== backgroundBannerImage || $[53] !== imageAltText || $[54] !== imagePath) {
    t24 = imagePath && !backgroundBannerImage && <div className="overflow-hidden rounded-2xl"><Image src={imagePath} quality={75} alt={imageAltText} className="object-contain object-bottom bg-blend-color" width={400} height={500} loading="eager" fetchPriority="high" /></div>;
    $[52] = backgroundBannerImage;
    $[53] = imageAltText;
    $[54] = imagePath;
    $[55] = t24;
  } else {
    t24 = $[55];
  }
  let t25;
  if ($[56] !== t23 || $[57] !== t24) {
    t25 = <div className="relative hidden sm:col-span-4 sm:block">{t23}{t24}</div>;
    $[56] = t23;
    $[57] = t24;
    $[58] = t25;
  } else {
    t25 = $[58];
  }
  let t26;
  if ($[59] !== t22 || $[60] !== t25) {
    t26 = <div className="grid min-h-87 grid-cols-1 items-center gap-5 sm:grid-cols-12">{t22}{t25}</div>;
    $[59] = t22;
    $[60] = t25;
    $[61] = t26;
  } else {
    t26 = $[61];
  }
  let t27;
  if ($[62] !== breadcrumbs) {
    t27 = <Breadcrumbs breadcrumbs={breadcrumbs} className="sm:absolute" />;
    $[62] = breadcrumbs;
    $[63] = t27;
  } else {
    t27 = $[63];
  }
  let t28;
  if ($[64] !== T0 || $[65] !== backgroundBannerImage || $[66] !== t26 || $[67] !== t27 || $[68] !== t4 || $[69] !== t5) {
    t28 = <T0 id={t4} className={t5} bgImage={backgroundBannerImage} bgImageProps={t6}>{t7}{t26}{t27}</T0>;
    $[64] = T0;
    $[65] = backgroundBannerImage;
    $[66] = t26;
    $[67] = t27;
    $[68] = t4;
    $[69] = t5;
    $[70] = t28;
  } else {
    t28 = $[70];
  }
  return t28;
};
export default BannerWithBackgroundColor;
function _temp(item, index) {
  return <Box key={`accreditation-by-logo-${index}`} className="flex h-10 items-center justify-center">{item?.imagePath && <Image src={item?.imagePath || ""} alt={item?.text || ""} width={90} height={40} quality={75} className="h-auto w-auto max-w-32 object-contain" />}</Box>;
}